import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchDoNotCall, fetchSettings, saveSettings, setDoNotCall } from "@services/reactivation";
import {
  createOutreachTreatment, fetchOutreachTreatments, updateOutreachTreatment,
} from "@services/outreach";

const reason = (e) => e?.message || "Something went wrong.";

/**
 * Everything the settings tab edits.
 *
 * `settings` is what the server holds; `draft` is what the clinic is editing.
 * Nothing is written until save() is called, and the server's normalised answer
 * then replaces both, so the screen shows what was actually stored.
 *
 * The three requests are independent, so they are settled independently and
 * each section keeps its own error: a clinic with no PMS still gets its
 * procedures (/outreach/treatments is the one endpoint here that does not need
 * one) even though the settings document and the do-not-call list both fail.
 */
export function useReactivationSettings(clinicId) {
  const [settings, setSettings] = useState(null);
  const [draft, setDraftState] = useState(null);
  const [treatments, setTreatments] = useState([]);
  const [doNotCall, setDoNotCallList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settingsError, setSettingsError] = useState(null);
  const [treatmentsError, setTreatmentsError] = useState(null);
  const [doNotCallError, setDoNotCallError] = useState(null);
  // Every load is stamped; a resolution whose stamp is no longer the current
  // one belongs to a clinic the user has left, and is thrown away.
  const loadReqRef = useRef(0);

  const clearSettingsError = useCallback(() => setSettingsError(null), []);
  const clearTreatmentsError = useCallback(() => setTreatmentsError(null), []);
  const clearDoNotCallError = useCallback(() => setDoNotCallError(null), []);

  const loadTreatments = useCallback(async () => {
    setTreatments(await fetchOutreachTreatments({ clinicId }));
  }, [clinicId]);

  const loadDoNotCall = useCallback(async () => {
    setDoNotCallList(await fetchDoNotCall(clinicId));
  }, [clinicId]);

  const reload = useCallback(async () => {
    const reqId = ++loadReqRef.current;
    setLoading(true);
    const [loaded, rows, flagged] = await Promise.allSettled([
      fetchSettings(clinicId), fetchOutreachTreatments({ clinicId }), fetchDoNotCall(clinicId),
    ]);
    if (loadReqRef.current !== reqId) return;

    if (loaded.status === "fulfilled") {
      setSettings(loaded.value);
      setDraftState(loaded.value);
      setSettingsError(null);
    } else {
      setSettingsError(reason(loaded.reason));
    }

    if (rows.status === "fulfilled") {
      setTreatments(rows.value);
      setTreatmentsError(null);
    } else {
      setTreatmentsError(reason(rows.reason));
    }

    if (flagged.status === "fulfilled") {
      setDoNotCallList(flagged.value);
      setDoNotCallError(null);
    } else {
      setDoNotCallError(reason(flagged.reason));
    }

    setLoading(false);
  }, [clinicId]);

  useEffect(() => { reload(); }, [reload]);

  const setDraft = useCallback((changes) => {
    setDraftState((current) => ({ ...current, ...changes }));
  }, []);

  const discard = useCallback(() => setDraftState(settings), [settings]);

  // Only the fields that differ are sent, so an untouched field is never
  // overwritten by a stale value, and a clean form never sends an empty body.
  const changes = useMemo(() => {
    if (!settings || !draft) return {};
    const diff = {};
    if (draft.cooldownDays !== settings.cooldownDays) diff.cooldownDays = draft.cooldownDays;
    if (draft.smsEnabled !== settings.smsEnabled) diff.smsEnabled = draft.smsEnabled;
    if (draft.smsTemplate !== settings.smsTemplate) diff.smsTemplate = draft.smsTemplate;
    if (JSON.stringify(draft.attemptGapsDays) !== JSON.stringify(settings.attemptGapsDays)) {
      diff.attemptGapsDays = draft.attemptGapsDays;
    }
    if (JSON.stringify(draft.callingSlots) !== JSON.stringify(settings.callingSlots)) {
      diff.callingSlots = draft.callingSlots;
    }
    if (JSON.stringify(draft.callingDays) !== JSON.stringify(settings.callingDays)) {
      diff.callingDays = draft.callingDays;
    }
    if (draft.dailyReportEnabled !== settings.dailyReportEnabled) {
      diff.dailyReportEnabled = draft.dailyReportEnabled;
    }
    if (JSON.stringify(draft.dailyReportRecipients)
        !== JSON.stringify(settings.dailyReportRecipients)) {
      diff.dailyReportRecipients = draft.dailyReportRecipients;
    }
    if (draft.dailyReportTime !== settings.dailyReportTime) {
      diff.dailyReportTime = draft.dailyReportTime;
    }
    if (draft.transferHoldMinutes !== settings.transferHoldMinutes) {
      diff.transferHoldMinutes = draft.transferHoldMinutes;
    }
    return diff;
  }, [settings, draft]);

  const dirty = Object.keys(changes).length > 0;

  const save = useCallback(async () => {
    if (!dirty) return;
    setSaving(true);
    setSettingsError(null);
    try {
      const saved = await saveSettings({ clinicId, ...changes });
      setSettings(saved);
      setDraftState(saved);
    } catch (e) {
      setSettingsError(reason(e));
    } finally {
      setSaving(false);
    }
  }, [clinicId, changes, dirty]);

  // The write and the refetch are reported apart: a create the server accepted
  // is a success even when reading the list back fails, or the clinic clears
  // the form's contents, adds the same procedure again, and collects a 409 for
  // a row that does exist.
  const addTreatment = useCallback(async (fields) => {
    setTreatmentsError(null);
    try {
      await createOutreachTreatment({ clinicId, ...fields });
    } catch (e) {
      setTreatmentsError(reason(e));
      return false;
    }
    try {
      await loadTreatments();
    } catch (e) {
      setTreatmentsError(reason(e));
    }
    return true;
  }, [clinicId, loadTreatments]);

  const editTreatment = useCallback(async (treatmentId, fields) => {
    setTreatmentsError(null);
    try {
      await updateOutreachTreatment(treatmentId, { clinicId, ...fields });
    } catch (e) {
      setTreatmentsError(reason(e));
      return false;
    }
    try {
      await loadTreatments();
    } catch (e) {
      setTreatmentsError(reason(e));
    }
    return true;
  }, [clinicId, loadTreatments]);

  const allowCalls = useCallback(async (externalPatientId) => {
    setDoNotCallError(null);
    try {
      await setDoNotCall(externalPatientId, { doNotCall: false, clinicId });
    } catch (e) {
      setDoNotCallError(reason(e));
      return false;
    }
    try {
      await loadDoNotCall();
    } catch (e) {
      setDoNotCallError(reason(e));
    }
    return true;
  }, [clinicId, loadDoNotCall]);

  return {
    settings, draft, setDraft, discard, dirty, save, saving,
    treatments, addTreatment, editTreatment,
    doNotCall, allowCalls,
    loading, reload,
    settingsError, treatmentsError, doNotCallError,
    clearSettingsError, clearTreatmentsError, clearDoNotCallError,
  };
}
